import { useState, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { generateBillNumber } from "@/lib/naming";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtQty = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

interface Props {
  wo: any;
  woLines: any[]; // lines for this WO only, with unit_schedule joined
  sovLines: any[];
  projectId: string;
  project?: any;
  onBillGenerated: () => void;
}

export const WOBillingGrid = ({ wo, woLines, sovLines, projectId, project, onBillGenerated }: Props) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Group WO lines by unit and SOV line
  const unitLines = woLines.filter((wol: any) => wol.unit_id);

  // Get unique units and SOV lines from this WO's lines
  const woUnits = useMemo(() => {
    const unitMap = new Map<string, { id: string; number: string }>();
    unitLines.forEach((wol: any) => {
      if (wol.unit_id && wol.unit_schedule) {
        unitMap.set(wol.unit_id, { id: wol.unit_id, number: wol.unit_schedule.unit_number });
      }
    });
    return Array.from(unitMap.values()).sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
  }, [unitLines]);

  const woSovLines = useMemo(() => {
    const slIds = new Set(unitLines.map((wol: any) => wol.sov_line_id));
    return (sovLines ?? []).filter((sl: any) => slIds.has(sl.id));
  }, [unitLines, sovLines]);

  // Lookup: unitId+sovLineId -> woLine
  const lineMap = useMemo(() => {
    const m = new Map<string, any>();
    unitLines.forEach((wol: any) => {
      m.set(`${wol.unit_id}::${wol.sov_line_id}`, wol);
    });
    return m;
  }, [unitLines]);

  const getLine = (unitId: string, sovLineId: string) => lineMap.get(`${unitId}::${sovLineId}`);

  const toggleLine = (wolId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(wolId)) next.delete(wolId);
      else next.add(wolId);
      return next;
    });
  };

  const selectAllComplete = () => {
    const ids = unitLines
      .filter((wol: any) => wol.status === "complete")
      .map((wol: any) => wol.id);
    setSelectedIds(new Set(ids));
  };

  // Summary by SOV line
  const billSummary = useMemo(() => {
    const groups: Record<string, { sovLine: any; count: number; totalQty: number; totalValue: number; totalUnits: number }> = {};
    selectedIds.forEach(wolId => {
      const wol = unitLines.find((l: any) => l.id === wolId);
      if (!wol) return;
      const sl = woSovLines.find((s: any) => s.id === wol.sov_line_id);
      if (!sl) return;
      if (!groups[wol.sov_line_id]) {
        const totalForLine = unitLines.filter((l: any) => l.sov_line_id === wol.sov_line_id).length;
        groups[wol.sov_line_id] = { sovLine: sl, count: 0, totalQty: 0, totalValue: 0, totalUnits: totalForLine };
      }
      groups[wol.sov_line_id].count++;
      groups[wol.sov_line_id].totalQty += Number(wol.quantity);
      groups[wol.sov_line_id].totalValue += Number(wol.quantity) * Number(wol.contract_rate);
    });
    return Object.values(groups);
  }, [selectedIds, unitLines, woSovLines]);

  const subtotal = billSummary.reduce((s, g) => s + g.totalValue, 0);
  const retainagePct = Number(wo.retainage_pct ?? 10);
  const retainageAmt = subtotal * (retainagePct / 100);
  const netDue = subtotal - retainageAmt;

  const handleGenerateBill = async () => {
    if (selectedIds.size === 0) return;
    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      // Create the bill header
      const billNumber = await generateBillNumber(wo.id, wo.number);
      const { data: bill, error: billErr } = await supabase.from("bills").insert({
        company_id: companyId,
        project_id: projectId,
        work_order_id: wo.id,
        vendor_id: wo.contractor_id,
        bill_number: billNumber,
        bill_date: new Date().toISOString().split("T")[0],
        subtotal,
        retainage_percent: retainagePct,
        retainage_amount: retainageAmt,
        total: netDue,
        status: "draft",
        description: `${wo.number} — Draw`,
      } as any).select("id").single();

      if (billErr) throw billErr;

      // Create bill lines
      const billLines = Array.from(selectedIds).map(wolId => {
        const wol = unitLines.find((l: any) => l.id === wolId)!;
        return {
          bill_id: bill.id,
          company_id: companyId,
          description: `${wol.description} — Unit ${wol.unit_schedule?.unit_number ?? "?"}`,
          quantity: Number(wol.quantity),
          unit_price: Number(wol.contract_rate),
          line_total: Number(wol.quantity) * Number(wol.contract_rate),
          sov_line_id: wol.sov_line_id,
          work_order_line_id: wol.id,
        };
      });

      const { error: blErr } = await supabase.from("bill_lines").insert(billLines as any);
      if (blErr) throw blErr;

      // Update WO lines status to "billed" and set billed_amount + billed_qty
      const userId = (await supabase.auth.getUser()).data.user?.id;
      for (const wolId of selectedIds) {
        const wol = unitLines.find((l: any) => l.id === wolId)!;
        await supabase.from("work_order_lines").update({
          status: "billed",
          billed_amount: Number(wol.quantity) * Number(wol.contract_rate),
        } as any).eq("id", wolId);
      }

      // Audit log — bill is created as draft; use "Send for Approval" in the Bills tab to submit
      await supabase.from("ops_audit_log").insert({
        table_name: "bills",
        record_id: bill.id,
        action: "bill_created",
        new_status: "draft",
        changed_by: userId,
        metadata: {
          wo_number: wo.number,
          bill_number: billNumber,
          lines_count: selectedIds.size,
          subtotal,
          retainage: retainageAmt,
          total: netDue,
          source: "billing_grid",
        },
      } as any);

      toast.success(`Bill ${billNumber} created (draft) — ${selectedIds.size} unit-lines | Net: ${fmt(netDue)}`);
      setSelectedIds(new Set());
      onBillGenerated();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (woUnits.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No unit-level lines on this WO. Use the standard billing flow.</p>;
  }

  return (
    <TooltipProvider>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Bill — {wo.number}</span>
          <Button variant="outline" size="sm" onClick={selectAllComplete}>Select All Complete</Button>
        </div>

        <ScrollArea className="max-h-[400px]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 bg-background">
                <tr>
                  <th className="border p-1.5 text-left text-xs font-medium text-muted-foreground sticky left-0 bg-background min-w-[80px]">Unit</th>
                  {woSovLines.map((sl: any) => (
                    <th key={sl.id} className="border p-1.5 text-center text-xs font-medium min-w-[120px]">
                      <div className="font-mono text-[10px] text-muted-foreground">{sl.item_code ?? `#${sl.line_number}`}</div>
                      <div className="truncate">{sl.description}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {woUnits.map(unit => (
                  <tr key={unit.id}>
                    <td className="border p-1.5 font-mono text-xs font-medium sticky left-0 bg-background">
                      {unit.number}
                    </td>
                    {woSovLines.map((sl: any) => {
                      const wol = getLine(unit.id, sl.id);

                      if (!wol) {
                        return <td key={sl.id} className="border p-1 text-center text-muted-foreground/30">—</td>;
                      }

                      const qty = Number(wol.quantity);
                      const uom = wol.unit ?? sl.unit_of_measure ?? "";
                      const isComplete = wol.status === "complete";
                      const isBilled = wol.status === "billed";
                      const isPending = wol.status === "pending";
                      const isSelected = selectedIds.has(wol.id);

                      if (isBilled) {
                        return (
                          <td key={sl.id} className="border p-1 text-center bg-muted/30">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground text-xs">● billed</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{fmtQty(qty)} {uom} · {fmt(qty * Number(wol.contract_rate))}</p>
                              </TooltipContent>
                            </Tooltip>
                          </td>
                        );
                      }

                      if (isPending) {
                        return (
                          <td key={sl.id} className="border p-1 text-center">
                            <span className="text-xs text-muted-foreground">
                              ☐ {fmtQty(qty)} {uom}
                            </span>
                          </td>
                        );
                      }

                      // complete — checkable
                      return (
                        <td
                          key={sl.id}
                          className={cn(
                            "border p-1 text-center cursor-pointer transition-colors",
                            isSelected ? "bg-primary/10" : "hover:bg-accent/30"
                          )}
                          onClick={() => toggleLine(wol.id)}
                        >
                          <div className="flex items-center justify-center gap-1">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleLine(wol.id)}
                              onClick={e => e.stopPropagation()}
                            />
                            <span className="text-xs">{fmtQty(qty)} {uom}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollArea>

        {/* Bill Summary */}
        {billSummary.length > 0 && (
          <div className="border rounded-md p-3 space-y-1 bg-muted/30 text-sm">
            {billSummary.map(g => (
              <div key={g.sovLine.id} className="flex justify-between">
                <span>
                  <span className="font-mono text-xs text-muted-foreground mr-1">{g.sovLine.item_code ?? `#${g.sovLine.line_number}`}</span>
                  {g.sovLine.description} · {g.count} unit{g.count !== 1 ? "s" : ""} × {fmtQty(g.totalQty / g.count)} {g.sovLine.unit_of_measure ?? ""}
                </span>
                <span className="font-medium">= {fmt(g.totalValue)}</span>
              </div>
            ))}
            <div className="border-t pt-1 space-y-0.5">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span className="font-medium">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Retainage ({retainagePct}%)</span>
                <span>-{fmt(retainageAmt)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-1">
                <span>Net due</span>
                <span>{fmt(netDue)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleGenerateBill} disabled={busy || selectedIds.size === 0}>
            {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Generating…</> : `Generate Bill →`}
          </Button>
        </div>
      </div>
    </TooltipProvider>
  );
};
